import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function Logo() {
  return (
    <svg width="34" height="34" viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id="hg" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#0b2545"/>
          <stop offset="1" stopColor="#1a3a6b"/>
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="10" fill="url(#hg)"/>
      <path d="M6 46 L18 30 L26 38 L36 22 L46 34 L58 20 L58 52 L6 52 Z" fill="#c66b3d" opacity="0.9"/>
      <path d="M6 46 L18 30 L26 38 L36 22 L46 34 L58 20" fill="none" stroke="#f5f5f2" strokeWidth="2" strokeLinejoin="round"/>
      <circle cx="46" cy="34" r="3" fill="#f5f5f2"/>
      <circle cx="36" cy="22" r="2" fill="#f5f5f2"/>
    </svg>
  );
}

export default function Header() {
  const { t, i18n } = useTranslation();
  const { lang = 'ar' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isHome = /^\/(ar|en)\/?$/.test(location.pathname);

  const goAnchor = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (!isHome) {
      navigate(`/${lang}#${id}`);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const switchLang = (next) => {
    if (next === lang) return;
    // preserve pathname tail after /:lang
    const rest = location.pathname.replace(/^\/(ar|en)/, '') || '';
    const newPath = `/${next}${rest}`;
    // language switch should NOT scroll to top (handled by ScrollToTop)
    navigate(newPath);
    i18n.changeLanguage(next);
  };

  const navLinks = [
    { id: 'overview', label: t('nav.overview') },
    { id: 'why', label: t('nav.why') },
    { id: 'process', label: t('nav.process') },
    { id: 'transparency', label: t('nav.transparency') },
    { id: 'contact', label: t('nav.contact') },
  ];

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-shadow ${scrolled ? 'shadow-[0_1px_0_0_#e5e7eb]' : ''}`}
      style={{ borderBottom: '1px solid var(--gd-line)' }}
    >
      <div className="container-x flex items-center justify-between" style={{ height: 72 }}>
        <Link to={`/${lang}`} className="flex items-center gap-3" aria-label={t('brand.name')}>
          <Logo />
          <div className="leading-tight">
            <div className="font-serif" style={{ fontSize: 20, color: 'var(--gd-navy)', fontWeight: 600, letterSpacing: '-0.01em' }}>
              {t('brand.name')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--gd-mineral)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {t('brand.tagline')}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={goAnchor(l.id)}
              className="link-underline"
              style={{ color: 'var(--gd-ink)', fontSize: 14, fontWeight: 500 }}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center border border-[#e5e7eb]" role="group" aria-label={t('nav.language')}>
            <button
              onClick={() => switchLang('ar')}
              className="px-3 py-1.5"
              style={{
                fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
                background: lang === 'ar' ? 'var(--gd-navy)' : 'transparent',
                color: lang === 'ar' ? 'white' : 'var(--gd-navy)',
              }}
              aria-pressed={lang === 'ar'}
            >AR</button>
            <button
              onClick={() => switchLang('en')}
              className="px-3 py-1.5"
              style={{
                fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
                background: lang === 'en' ? 'var(--gd-navy)' : 'transparent',
                color: lang === 'en' ? 'white' : 'var(--gd-navy)',
                borderLeft: '1px solid #e5e7eb',
              }}
              aria-pressed={lang === 'en'}
            >EN</button>
          </div>
          <a href={`#contact`} onClick={goAnchor('contact')} className="btn-primary" style={{ padding: '10px 18px' }}>
            {t('buttons.contactUs')}
          </a>
        </div>

        <button
          className="lg:hidden inline-flex items-center justify-center"
          style={{ color: 'var(--gd-navy)', padding: 8 }}
          aria-label={open ? t('nav.closeMenu') : t('nav.openMenu')}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={`hamburger ${open ? 'open' : ''}`}>
            <span></span><span></span><span></span>
          </span>
        </button>
      </div>

      {open && (
        <div className="lg:hidden" style={{ borderTop: '1px solid var(--gd-line)', background: 'white' }}>
          <div className="container-x py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={goAnchor(l.id)}
                style={{ color: 'var(--gd-ink)', padding: '10px 0', fontWeight: 500, fontSize: 15, borderBottom: '1px solid var(--gd-line)' }}
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <span style={{ fontSize: 12, color: 'var(--gd-mineral)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {t('nav.language')}
              </span>
              <div className="flex items-center border border-[#e5e7eb]">
                <button onClick={() => switchLang('ar')} className="px-3 py-1.5" style={{
                  fontSize: 12, fontWeight: 700,
                  background: lang === 'ar' ? 'var(--gd-navy)' : 'transparent',
                  color: lang === 'ar' ? 'white' : 'var(--gd-navy)',
                }}>AR</button>
                <button onClick={() => switchLang('en')} className="px-3 py-1.5" style={{
                  fontSize: 12, fontWeight: 700,
                  background: lang === 'en' ? 'var(--gd-navy)' : 'transparent',
                  color: lang === 'en' ? 'white' : 'var(--gd-navy)',
                  borderLeft: '1px solid #e5e7eb',
                }}>EN</button>
              </div>
            </div>
            <a href={`#contact`} onClick={goAnchor('contact')} className="btn-primary mt-2">
              {t('buttons.contactUs')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
